import React, { useMemo } from 'react';
import Pivot from '../Pivot';
import { appendPivotSubtotals } from './pivot-subtotals';

// Mapping from RQL group field → body-row keys that identify the row dim tuple.
const ROW_DIM_KEYS_BY_GROUP = {
  project: ['projectId', 'projectName'],
  'project.practice': ['projectPracticeId', 'projectPracticeName'],
  'project.businessUnit': ['projectBusinessUnitId', 'projectBusinessUnitName'],
  client: ['clientId', 'clientName'],
  'client.practice': ['clientPracticeId', 'clientPracticeName'],
  'client.businessUnit': ['clientBusinessUnitId', 'clientBusinessUnitName'],
};

const TOOLTIP_CONFIG = {
  columns: ['Budget', 'Actuals', 'Left', 'Used'],
  groups: [
    { label: 'Hours', cells: ['hoursBudget', 'hours', 'hoursBudgetLeft', 'hoursBudgetUsedRatio'] },
    {
      label: 'Billable Hours',
      cells: ['billableHoursBudget', 'billableHours', 'billableHoursBudgetLeft', 'billableHoursBudgetUsedRatio'],
    },
    {
      label: 'Non-Billable Hours',
      cells: [
        'nonBillableHoursBudget',
        'nonBillableHours',
        'nonBillableHoursBudgetLeft',
        'nonBillableHoursBudgetUsedRatio',
      ],
    },
    {
      label: 'Internal Hours',
      cells: ['internalHoursBudget', 'internalHours', 'internalHoursBudgetLeft', 'internalHoursBudgetUsedRatio'],
    },
    {
      label: 'Services Revenue',
      cells: [
        'servicesRevenueBudget',
        'servicesRevenue',
        'servicesRevenueBudgetLeft',
        'servicesRevenueBudgetUsedRatio',
      ],
    },
    {
      label: 'Expenses Revenue',
      cells: [
        'expensesRevenueBudget',
        'expensesRevenue',
        'expensesRevenueBudgetLeft',
        'expensesRevenueBudgetUsedRatio',
      ],
    },
    {
      label: 'Product Revenue',
      cells: [
        'productItemsRevenueBudget',
        'productItemsRevenue',
        'productItemsRevenueBudgetLeft',
        'productItemsRevenueBudgetUsedRatio',
      ],
    },
    {
      label: 'Other Items Revenue',
      cells: [
        'otherItemsRevenueBudget',
        'otherItemsRevenue',
        'otherItemsRevenueBudgetLeft',
        'otherItemsRevenueBudgetUsedRatio',
      ],
    },
    {
      label: 'Earned Revenue',
      cells: ['revenueBudget', 'revenue', 'revenueBudgetLeft', 'revenueBudgetUsedRatio'],
    },
    {
      label: 'Services Revenue (Converted)',
      cells: [
        'convertedServicesRevenueBudget',
        'convertedServicesRevenue',
        'convertedServicesRevenueBudgetLeft',
        // Used ratio is unit-free — native ratio is mathematically identical.
        'servicesRevenueBudgetUsedRatio',
      ],
      when: { fieldsDiffer: ['currency', 'convertedCurrency'] },
    },
    {
      label: 'Expenses Revenue (Converted)',
      cells: [
        'convertedExpensesRevenueBudget',
        'convertedExpensesRevenue',
        'convertedExpensesRevenueBudgetLeft',
        'expensesRevenueBudgetUsedRatio',
      ],
      when: { fieldsDiffer: ['currency', 'convertedCurrency'] },
    },
    {
      label: 'Product Revenue (Converted)',
      cells: [
        'convertedProductItemsRevenueBudget',
        'convertedProductItemsRevenue',
        'convertedProductItemsRevenueBudgetLeft',
        'productItemsRevenueBudgetUsedRatio',
      ],
      when: { fieldsDiffer: ['currency', 'convertedCurrency'] },
    },
    {
      label: 'Other Items Revenue (Converted)',
      cells: [
        'convertedOtherItemsRevenueBudget',
        'convertedOtherItemsRevenue',
        'convertedOtherItemsRevenueBudgetLeft',
        'otherItemsRevenueBudgetUsedRatio',
      ],
      when: { fieldsDiffer: ['currency', 'convertedCurrency'] },
    },
    {
      label: 'Earned Revenue (Converted)',
      cells: ['convertedRevenueBudget', 'convertedRevenue', 'convertedRevenueBudgetLeft', 'revenueBudgetUsedRatio'],
      when: { fieldsDiffer: ['currency', 'convertedCurrency'] },
    },
    { label: 'Realization Rate', cells: ['realizationRateBudget', 'realizationRate', null, null] },
    { label: 'Effective Bill Rate', cells: ['effectiveRateBudget', 'effectiveRate', null, null] },
    {
      label: 'Effective Bill Rate (Converted)',
      cells: ['convertedEffectiveRateBudget', 'convertedEffectiveRate', null, null],
      when: { fieldsDiffer: ['currency', 'convertedCurrency'] },
    },
  ],
};

// Compute subtotals from flat body rows. Sums for sum-able fields, ratio-of-sums for ratios —
// mirrors the SQL totalUsing semantics. Lives in the frontend so the rules can evolve
// retroactively without touching historical messages.
function aggregate(group) {
  const sum = (key) => group.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
  const ratio = (numKey, denKey) => {
    const den = sum(denKey);
    return den ? sum(numKey) / den : null;
  };
  const round2 = (v) => (v == null ? v : Math.round(v * 100) / 100);

  const currencies = new Set(group.map((r) => r.currency).filter(Boolean));
  const currency = currencies.size === 1 ? [...currencies][0] : null;
  const convertedCurrencies = new Set(group.map((r) => r.convertedCurrency).filter(Boolean));
  const convertedCurrency = convertedCurrencies.size === 1 ? [...convertedCurrencies][0] : null;

  return {
    currency,
    convertedCurrency,

    hours: sum('hours'),
    billableHours: sum('billableHours'),
    nonBillableHours: sum('nonBillableHours'),
    internalHours: sum('internalHours'),

    realizationRate: ratio('billableHours', 'hours'),
    realizationRateBudget: ratio('billableHoursBudget', 'hoursBudget'),
    effectiveRate: round2(ratio('servicesRevenue', 'hours')),
    effectiveRateBudget: round2(ratio('servicesRevenueBudget', 'hoursBudget')),
    convertedEffectiveRate: round2(ratio('convertedServicesRevenue', 'hours')),
    convertedEffectiveRateBudget: round2(ratio('convertedServicesRevenueBudget', 'hoursBudget')),

    servicesRevenue: sum('servicesRevenue'),
    expensesRevenue: sum('expensesRevenue'),
    productItemsRevenue: sum('productItemsRevenue'),
    otherItemsRevenue: sum('otherItemsRevenue'),
    revenue: round2(
      sum('servicesRevenue') + sum('expensesRevenue') + sum('productItemsRevenue') + sum('otherItemsRevenue'),
    ),

    convertedServicesRevenue: sum('convertedServicesRevenue'),
    convertedExpensesRevenue: sum('convertedExpensesRevenue'),
    convertedProductItemsRevenue: sum('convertedProductItemsRevenue'),
    convertedOtherItemsRevenue: sum('convertedOtherItemsRevenue'),
    convertedRevenue: round2(
      sum('convertedServicesRevenue') +
        sum('convertedExpensesRevenue') +
        sum('convertedProductItemsRevenue') +
        sum('convertedOtherItemsRevenue'),
    ),

    billableHoursBudget: sum('billableHoursBudget'),
    nonBillableHoursBudget: sum('nonBillableHoursBudget'),
    internalHoursBudget: sum('internalHoursBudget'),
    hoursBudget: sum('hoursBudget'),

    billableHoursBudgetLeft: sum('billableHoursBudget') - sum('billableHours'),
    nonBillableHoursBudgetLeft: sum('nonBillableHoursBudget') - sum('nonBillableHours'),
    internalHoursBudgetLeft: sum('internalHoursBudget') - sum('internalHours'),
    hoursBudgetLeft: sum('hoursBudget') - sum('hours'),

    billableHoursBudgetUsedRatio: ratio('billableHours', 'billableHoursBudget'),
    nonBillableHoursBudgetUsedRatio: ratio('nonBillableHours', 'nonBillableHoursBudget'),
    internalHoursBudgetUsedRatio: ratio('internalHours', 'internalHoursBudget'),
    hoursBudgetUsedRatio: ratio('hours', 'hoursBudget'),

    servicesRevenueBudget: sum('servicesRevenueBudget'),
    expensesRevenueBudget: sum('expensesRevenueBudget'),
    productItemsRevenueBudget: sum('productItemsRevenueBudget'),
    otherItemsRevenueBudget: sum('otherItemsRevenueBudget'),
    revenueBudget: sum('revenueBudget'),

    servicesRevenueBudgetLeft: sum('servicesRevenueBudget') - sum('servicesRevenue'),
    expensesRevenueBudgetLeft: sum('expensesRevenueBudget') - sum('expensesRevenue'),
    productItemsRevenueBudgetLeft: sum('productItemsRevenueBudget') - sum('productItemsRevenue'),
    otherItemsRevenueBudgetLeft: sum('otherItemsRevenueBudget') - sum('otherItemsRevenue'),
    revenueBudgetLeft:
      sum('revenueBudget') -
      (sum('servicesRevenue') + sum('expensesRevenue') + sum('productItemsRevenue') + sum('otherItemsRevenue')),

    servicesRevenueBudgetUsedRatio: ratio('servicesRevenue', 'servicesRevenueBudget'),
    expensesRevenueBudgetUsedRatio: ratio('expensesRevenue', 'expensesRevenueBudget'),
    productItemsRevenueBudgetUsedRatio: ratio('productItemsRevenue', 'productItemsRevenueBudget'),
    otherItemsRevenueBudgetUsedRatio: ratio('otherItemsRevenue', 'otherItemsRevenueBudget'),
    revenueBudgetUsedRatio:
      sum('revenueBudget') > 0
        ? (sum('servicesRevenue') + sum('expensesRevenue') + sum('productItemsRevenue') + sum('otherItemsRevenue')) /
          sum('revenueBudget')
        : null,

    convertedServicesRevenueBudget: sum('convertedServicesRevenueBudget'),
    convertedExpensesRevenueBudget: sum('convertedExpensesRevenueBudget'),
    convertedProductItemsRevenueBudget: sum('convertedProductItemsRevenueBudget'),
    convertedOtherItemsRevenueBudget: sum('convertedOtherItemsRevenueBudget'),
    convertedRevenueBudget: sum('convertedRevenueBudget'),

    convertedServicesRevenueBudgetLeft: sum('convertedServicesRevenueBudget') - sum('convertedServicesRevenue'),
    convertedExpensesRevenueBudgetLeft: sum('convertedExpensesRevenueBudget') - sum('convertedExpensesRevenue'),
    convertedProductItemsRevenueBudgetLeft:
      sum('convertedProductItemsRevenueBudget') - sum('convertedProductItemsRevenue'),
    convertedOtherItemsRevenueBudgetLeft: sum('convertedOtherItemsRevenueBudget') - sum('convertedOtherItemsRevenue'),
    convertedRevenueBudgetLeft:
      sum('convertedRevenueBudget') -
      (sum('convertedServicesRevenue') +
        sum('convertedExpensesRevenue') +
        sum('convertedProductItemsRevenue') +
        sum('convertedOtherItemsRevenue')),
  };
}

export default function RevenueMonthlyBudgetPivot({ data, fields, meta, pagination, filename, cellValue }) {
  const enrichedData = useMemo(() => {
    const group = meta?.applied?.input?.group ?? [];
    const hasPeriod = group.some((g) => g.field === 'period');
    const rowDimGroups = group.filter((g) => g.field !== 'period');
    if (!hasPeriod || rowDimGroups.length === 0) return data;

    const rowDimKeys = rowDimGroups.flatMap((g) => ROW_DIM_KEYS_BY_GROUP[g.field] ?? []);
    if (rowDimKeys.length === 0) return data;

    return appendPivotSubtotals({
      rows: data,
      rowDimKeys,
      periodKeys: ['periodStart', 'periodEnd'],
      aggregate,
    });
  }, [data, meta]);

  return (
    <Pivot
      data={enrichedData}
      fields={fields}
      meta={meta}
      pagination={pagination}
      filename={filename}
      tooltipConfig={TOOLTIP_CONFIG}
      cellValue={cellValue}
    />
  );
}
